import React, { useState, useContext, useRef } from 'react'
import { WorkspaceContext } from '../WorkspaceContext'
import UploadFolderIcon from '../../../shared/components/svgIcons/UploadFolderIcon';
import './addnew.scss'
import UploadFileIcon from '../../../shared/components/svgIcons/UploadFileIcon';

import useCloseOnClickAway from '../../../shared/hooks/UseCloseOnClickAway';
import AddFileIcon from '../../../shared/components/svgIcons/AddFileIcon';
import NewFolderIcon from '../../../shared/components/svgIcons/NewFolderIcon';
import { useDriveView } from '../DriveViewContext';
import { useUploadEngine } from './UploadEngineContext';
import { MAX_ROOM_ITEMS, FOLDER_LIMIT, ROOM_STORAGE_LIMIT_BYTES } from './folderUploadLimits';

const AddNewPopup = () => {

    const ctx = useContext(WorkspaceContext);
    if (!ctx) throw new Error("Workspace must be wrapped in WorkspaceContext.Provider");
    const { Snippets, globalNewFilePopup, setGlobalNewFilePopup, globalNewSnipPopup, setGlobalNewSnipPopup, sideBarOpen, setLimitMessage, setLimitPopup, setGlobalNewFolderPopup, Folders, addNewPopup, setAddNewPopup, Files, uiMode, roomStorage } = ctx;
    const { beginUpload, isUploading, hasOwnerBypass } = useUploadEngine();
    const { folderId: driveFolderId } = useDriveView();
    const baseFolderId = uiMode === 'files' ? driveFolderId : null;

    const [hideWhileUploading, setHideWhileUploading] = useState(false);
    const [hideForLimitPopup, setHideForLimitPopup] = useState(false);
    const fileLimitPopupOpenRef = useRef(false);

    const popupRef = useRef<HTMLDivElement | null>(null);

    useCloseOnClickAway({
        targetRef: popupRef,
        onClose: () => {
            if (!isUploading && !fileLimitPopupOpenRef.current) {
                setAddNewPopup(false);
            }
        }
    })



    const newFolderClick = () => {
        if (!hasOwnerBypass && Folders.length >= FOLDER_LIMIT) {
            setLimitPopup(true);
            setLimitMessage('You have reached the maximum number of folders. You must delete a folder to create a new one.')
        } else {
            setAddNewPopup(false);
            setGlobalNewFolderPopup(true)

        }
    }

    const newSnipClick = () => {
        if (!hasOwnerBypass && Snippets.length + Files.length >= MAX_ROOM_ITEMS) {
            setLimitPopup(true);
            setLimitMessage('File limit reached');
        } else {
            if (globalNewFilePopup) {
                setGlobalNewFilePopup(false)
            }
            setAddNewPopup(false);
            setGlobalNewSnipPopup(true);
        }
    }


    const [pickerMode, setPickerMode] = useState('');

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const alreadyAtStorageLimit = () => !hasOwnerBypass && roomStorage >= ROOM_STORAGE_LIMIT_BYTES;
    const alreadyAtItemLimit = () => !hasOwnerBypass && Snippets.length + Files.length >= MAX_ROOM_ITEMS;

    const setFileModeClick = () => {
        if (alreadyAtStorageLimit()) {
            setLimitPopup(true);
            setLimitMessage('Storage Limit Reached');
            return;
        }

        if (alreadyAtItemLimit()) {
            setLimitPopup(true);
            setLimitMessage('File limit reached');
            return;
        }

        setPickerMode('file');
        fileInputRef.current?.click();

    }


    const folderInputRef = useRef<HTMLInputElement | null>(null);

    const setFolderModeClick = () => {
        if (alreadyAtStorageLimit()) {
            setLimitPopup(true);
            setLimitMessage('Storage Limit Reached');
            return;
        }

        if (alreadyAtItemLimit()) {
            setLimitPopup(true);
            setLimitMessage('File limit reached');
            return;
        }

        if (!hasOwnerBypass && Folders.length >= FOLDER_LIMIT) {
            setLimitPopup(true);
            setLimitMessage('You have reached the maximum number of folders. You must delete a folder to create a new one.');
            return;
        }

        setPickerMode('folder');
        folderInputRef.current?.click();
    }


    // Every actual upload call (validation, limits, the Confirm Upload popup,
    // S3/snippet upload mechanics) lives in UploadEngineContext now, shared
    // with workspace-wide drag-and-drop — this just wires the picker's
    // <input> selection into it and preserves this popup's own open/close and
    // "hide while uploading" chrome, exactly as before.
    const handleBrowse = async (
        e: React.ChangeEvent<HTMLInputElement>,
        mode: "file" | "folder"
    ) => {
        if (!e.target.files?.length) {
            // A folder pick can legitimately land here: browsers strip dotfiles/
            // dotfolders (e.g. a bare .git) from webkitdirectory results, so a
            // folder that's empty from the browser's point of view produces zero
            // files with no error of its own.
            if (mode === "folder") {
                setLimitPopup(true);
                setLimitMessage("That folder has no files to upload");
            }
            e.target.value = "";
            return;
        }
        if (isUploading) {
            e.target.value = "";
            return;
        }

        const selectedFiles = Array.from(e.target.files);
        e.target.value = "";

        await beginUpload(selectedFiles, mode, baseFolderId, {
            // Hide the picker as soon as an upload attempt (direct, or via the
            // Confirm Upload popup) actually starts — an early validation
            // failure (unsupported type / hard single-file limit) never fires
            // this, leaving the picker visible behind that toast, same as before.
            onEngaged: () => {
                fileLimitPopupOpenRef.current = true;
                setHideForLimitPopup(true);
                setHideWhileUploading(true);
            },
            onSettled: () => {
                fileLimitPopupOpenRef.current = false;
                setHideForLimitPopup(false);
                setHideWhileUploading(false);
                setAddNewPopup(false);
            },
        });
    };


    return (

        <>
        <div className={`add-new-popup fade-in ${(hideWhileUploading || hideForLimitPopup) ? 'uploading-hidden' : ''} ${sideBarOpen ? 'anp-so' : 'anp-sc'}`} ref={popupRef}>
            <div className='quick-flex' style={{ justifyContent: 'space-between' }}>
                <h3>Add New</h3>
                <div
                    onClick={() => {
                        if (!isUploading && !fileLimitPopupOpenRef.current) {
                            setAddNewPopup(false);
                        }
                    }}
                    className="ns-cancel"
                >
                    <svg className="x-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 111.01 111.01">
                        <g id="Layer_3" data-name="Layer 3">
                            <path
                                className="cls-1"
                                d="M232.21,289.66a10.1,10.1,0,0,1-14.31,0l-38.22-38.22-38.23,38.23a10.13,10.13,0,0,1-14.32-14.32l38.23-38.23L127.14,198.9a10.12,10.12,0,1,1,14.31-14.31l38.22,38.22,38.23-38.23a10.13,10.13,0,0,1,14.32,14.32L194,237.13l38.22,38.22A10.12,10.12,0,0,1,232.21,289.66Z"
                                transform="translate(-124.17 -181.62)"
                            />
                        </g>
                    </svg>
                </div>

            </div>

            <br />


            <div className='clickable new-popup-btn quick-flex' onClick={newSnipClick}>
                <AddFileIcon />
                <h4>New File</h4>
            </div>

            <div className='clickable new-popup-btn quick-flex' onClick={newFolderClick}>
                <NewFolderIcon />
                <h4>New Folder</h4>
            </div>

            <div onClick={isUploading ? undefined : setFileModeClick} className='clickable new-popup-btn quick-flex'>
                <input ref={fileInputRef}
                    style={{ display: 'none' }}
                    type='file'
                    multiple
                    onChange={(e) => handleBrowse(e, "file")}
                />
                <UploadFileIcon />
                <h4>Upload File</h4>
            </div>

            <div onClick={isUploading ? undefined : setFolderModeClick} className='clickable new-popup-btn quick-flex'>
                <input
                    ref={folderInputRef}
                    type="file"
                    {...({ webkitdirectory: "", directory: "" } as React.InputHTMLAttributes<HTMLInputElement>)}
                    onChange={(e) => handleBrowse(e, "folder")}
                    style={{ display: 'none' }}
                />
                <UploadFolderIcon />
                <h4>Upload Folder</h4>
            </div>

        </div>
        </>

    )
}

export default AddNewPopup
